import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import AppointmentTimeline from './components/AppointmentTimeline';
import PendingTasksPanel from './components/PendingTasksPanel';
import MetricsPanel from './components/MetricsPanel';
import QuickActionsPanel from './components/QuickActionsPanel';
import NotificationCenter from './components/NotificationCenter';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ProviderDashboard = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Mock data for appointments
  const [appointments] = useState([
    {
      id: 1,
      patientName: "Sarah Mitchell",
      patientId: "P001",
      time: "10:30 AM",
      type: "routine",
      location: "Video Call",
      status: "ready",
      priority: "medium",
      notes: "Follow-up for hypertension management",
      patientPreparation: true
    },
    {
      id: 2,
      patientName: "James Rodriguez",
      patientId: "P002",
      time: "11:15 AM",
      type: "urgent",
      location: "Video Call",
      status: "waiting",
      priority: "high",
      notes: "Chest pain evaluation - requires immediate attention",
      patientPreparation: true
    },
    {
      id: 3,
      patientName: "Emily Chen",
      patientId: "P003",
      time: "2:00 PM",
      type: "follow-up",
      location: "Video Call",
      status: "upcoming",
      priority: "low",
      notes: "Post-surgery recovery check",
      patientPreparation: false
    },
    {
      id: 4,
      patientName: "Michael Thompson",
      patientId: "P004",
      time: "3:30 PM",
      type: "consultation",
      location: "Video Call",
      status: "upcoming",
      priority: "medium",
      notes: "Initial consultation for diabetes management",
      patientPreparation: false
    }
  ]);

  // Mock data for pending tasks
  const [pendingPrescriptions] = useState([
    {
      id: 1,
      patientName: "Sarah Mitchell",
      medication: "Lisinopril",
      dosage: "10mg daily",
      urgency: "medium"
    },
    {
      id: 2,
      patientName: "James Rodriguez",
      medication: "Nitroglycerin",
      dosage: "0.4mg sublingual",
      urgency: "high"
    },
    {
      id: 3,
      patientName: "Emily Chen",
      medication: "Ibuprofen",
      dosage: "400mg TID",
      urgency: "low"
    }
  ]);

  const [labOrders] = useState([
    {
      id: 1,
      patientName: "Michael Thompson",
      testType: "HbA1c",
      status: "pending",
      orderedDate: "Sep 20"
    },
    {
      id: 2,
      patientName: "Sarah Mitchell",
      testType: "Lipid Panel",
      status: "results ready",
      orderedDate: "Sep 19"
    }
  ]);

  const [patientMessages] = useState([
    {
      id: 1,
      patientName: "Emily Chen",
      message: "Experiencing some discomfort at the surgical site. Should I be concerned?",
      time: "2 hours ago",
      unread: true
    },
    {
      id: 2,
      patientName: "James Rodriguez",
      message: "Thank you for the quick response yesterday. Feeling much better now.",
      time: "1 day ago",
      unread: false
    }
  ]);

  // Mock metrics data
  const [metrics] = useState({
    todayConsultations: 8,
    consultationChange: 12,
    pendingTasks: 15,
    taskChange: -5,
    safetyIncidents: 2,
    incidentChange: 0,
    patientSatisfaction: 94,
    satisfactionChange: 3
  });

  // Mock notifications
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'appointment',
      priority: 'high',
      title: 'Urgent Consultation Required',
      message: 'James Rodriguez is experiencing chest pain and needs immediate attention.',
      time: '5 min ago',
      read: false,
      actionRequired: true
    },
    {
      id: 2,
      type: 'prescription',
      priority: 'medium',
      title: 'Prescription Renewal Due',
      message: 'Sarah Mitchell\'s Lisinopril prescription expires in 2 days.',
      time: '1 hour ago',
      read: false,
      actionRequired: true
    },
    {
      id: 3,
      type: 'lab',
      priority: 'low',
      title: 'Lab Results Available',
      message: 'Lipid panel results are ready for Sarah Mitchell.',
      time: '3 hours ago',
      read: true,
      actionRequired: false
    },
    {
      id: 4,
      type: 'safety',
      priority: 'high',
      title: 'Safety Incident Reported',
      message: 'New workplace safety incident requires medical review.',
      time: '6 hours ago',
      read: false,
      actionRequired: true
    }
  ]);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Navigation handlers
  const handleJoinConsultation = (appointmentId) => {
    navigate('/video-consultation', { state: { appointmentId } });
  };

  const handleViewPatient = (patientId) => {
    navigate('/patient-records', { state: { patientId } });
  };

  const handleStartConsultation = () => {
    navigate('/video-consultation');
  };

  const handleCreatePrescription = () => {
    navigate('/prescription-management');
  };

  const handleViewRecords = () => {
    navigate('/patient-records');
  };

  const handleReportIncident = () => {
    navigate('/safety-incident-reporting');
  };

  const handleViewPrescriptions = () => {
    navigate('/prescription-management');
  };

  const handleViewLabOrders = () => {
    navigate('/patient-records', { state: { tab: 'lab-orders' } });
  };

  const handleViewMessages = () => {
    navigate('/patient-records', { state: { tab: 'messages' } });
  };

  const handleMarkAsRead = (notificationId) => {
    setNotifications(prev =>
      prev?.map(notification =>
        notification?.id === notificationId
          ? { ...notification, read: true }
          : notification
      )
    );
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev =>
      prev?.map(notification => ({ ...notification, read: true }))
    );
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Good morning, Dr. Johnson</h1>
                <p className="text-lg text-muted-foreground mt-1">
                  {formatDate(currentTime)} • {formatTime(currentTime)}
                </p>
              </div>
              <div className="hidden md:flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Wifi" size={16} className="text-success" />
                  <span>Connected</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Shield" size={16} className="text-success" />
                  <span>Secure Session</span>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Overview */}
          <div className="mb-8">
            <MetricsPanel metrics={metrics} />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Appointments and Quick Actions */}
            <div className="lg:col-span-2 space-y-8">
              <AppointmentTimeline
                appointments={appointments}
                onJoinConsultation={handleJoinConsultation}
                onViewPatient={handleViewPatient}
              />
              
              <QuickActionsPanel
                onStartConsultation={handleStartConsultation}
                onCreatePrescription={handleCreatePrescription}
                onViewRecords={handleViewRecords}
                onReportIncident={handleReportIncident}
              />
            </div>

            {/* Right Column - Pending Tasks and Notifications */}
            <div className="space-y-8">
              <PendingTasksPanel
                prescriptions={pendingPrescriptions}
                labOrders={labOrders}
                messages={patientMessages}
                onViewPrescriptions={handleViewPrescriptions}
                onViewLabOrders={handleViewLabOrders}
                onViewMessages={handleViewMessages}
              />
              
              <NotificationCenter
                notifications={notifications}
                onMarkAsRead={handleMarkAsRead}
                onMarkAllAsRead={handleMarkAllAsRead}
              />
            </div>
          </div>

          {/* Emergency Actions Bar */}
          <div className="fixed bottom-6 right-6 z-50">
            <div className="flex items-center space-x-3">
              <Button
                variant="destructive"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                className="shadow-alert"
              >
                Emergency Call
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="AlertTriangle"
                className="shadow-modal bg-background"
                onClick={handleReportIncident}
              >
                Report Incident
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProviderDashboard;